/**
 * SpaceNamePrompt — a small inline modal for entering a space name.
 *
 * Used for both "Create space" and "Rename space" flows.
 * iOS design language: 44px minimum touch targets, 1rem input (no Safari zoom),
 * spring-settle for entry animation. Calm: no nagging, no heavy chrome.
 *
 * A11y: role="dialog", focus trapped to input + buttons, Escape closes.
 * Reduced-motion: entry animation suppressed.
 *
 * Immutability: pure controlled component; no mutation of external state.
 */
import { useState, useEffect, useRef } from 'react';
import { MAX_SPACE_NAME_CHARS } from 'briefpane/schema';

interface SpaceNamePromptProps {
  /** Dialog title, e.g. "New space" or "Rename space". */
  readonly title: string;
  /** Initial value for rename flows. Leave empty for create. */
  readonly initialValue?: string;
  /** Inline error message to display (e.g. "Name already taken"). */
  readonly errorMessage?: string | null;
  /** Called when the user confirms the name (trimmed). */
  readonly onConfirm: (name: string) => void;
  /** Called when the user cancels. */
  readonly onCancel: () => void;
}

/** Detect prefers-reduced-motion. */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function SpaceNamePrompt({
  title,
  initialValue = '',
  errorMessage,
  onConfirm,
  onCancel,
}: SpaceNamePromptProps): React.ReactElement {
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);
  const reduced = prefersReducedMotion();

  // Auto-focus the input and select all text for rename flows.
  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  // Escape closes.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    }
    document.addEventListener('keydown', handleKeyDown, true);
    return () => { document.removeEventListener('keydown', handleKeyDown, true); };
  }, [onCancel]);

  function handleConfirm(): void {
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    onConfirm(trimmed);
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Enter') handleConfirm();
  }

  const isValid = value.trim().length > 0 && value.trim().length <= MAX_SPACE_NAME_CHARS;

  return (
    // Backdrop
    <div
      role="presentation"
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 600,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(4px)',
      }}
    >
      {/* Dialog panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => { e.stopPropagation(); }}
        style={{
          backgroundColor: 'var(--color-bg-tile)',
          borderRadius: '16px',
          padding: '20px 20px 16px',
          width: '280px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          animation: reduced ? undefined : 'spacePromptIn var(--dur-settle) var(--spring-settle) both',
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: 'var(--text-label)',
            fontWeight: 600,
            color: 'var(--color-text-primary)',
          }}
        >
          {title}
        </h2>

        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => { setValue(e.target.value); }}
          onKeyDown={handleKeyDown}
          maxLength={MAX_SPACE_NAME_CHARS}
          placeholder="Space name"
          aria-label="Space name"
          aria-invalid={!!errorMessage}
          aria-describedby={errorMessage ? 'space-name-error' : undefined}
          style={{
            // 1rem / 16px — prevents iOS Safari zoom-on-focus (ios-fluidity-spec §5.3)
            fontSize: '1rem',
            minHeight: '44px',
            padding: '0 12px',
            borderRadius: '10px',
            border: `1px solid ${errorMessage ? 'var(--color-negative-accent)' : 'var(--color-border)'}`,
            backgroundColor: 'var(--color-bg-input)',
            color: 'var(--color-text-primary)',
            outline: 'none',
          }}
        />

        {errorMessage && (
          <p
            id="space-name-error"
            role="alert"
            style={{
              margin: 0,
              fontSize: 'var(--text-delta)',
              color: 'var(--color-negative-accent)',
            }}
          >
            {errorMessage}
          </p>
        )}

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              minHeight: '44px',
              padding: '0 16px',
              borderRadius: '10px',
              border: '1px solid var(--color-border)',
              backgroundColor: 'transparent',
              color: 'var(--color-text-secondary)',
              fontSize: 'var(--text-delta)',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isValid}
            style={{
              minHeight: '44px',
              padding: '0 16px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: isValid ? 'var(--color-accent)' : 'var(--color-border)',
              color: isValid ? '#fff' : 'var(--color-text-dim)',
              fontSize: 'var(--text-delta)',
              fontWeight: 600,
              cursor: isValid ? 'pointer' : 'not-allowed',
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
